import "pages/StandardTreatment/shared/masterData.scss";
import "pages/MasterPengobatanStandar/index.scss";
import React, { useEffect, useMemo, useState } from "react";
import { getCoreRowModel, useReactTable } from "@tanstack/react-table";
import { toast } from "react-toastify";
import { FiPlus } from "react-icons/fi";
import Button from "components/atoms/Button";
import ActionButton from "components/atoms/ActionButton";
import InputField from "components/atoms/InputField";
import InputSelect from "components/atoms/InputSelect";
import TableDataComponent from "components/organisms/TableData";
import standardTreatmentService from "services/StandardTreatment";
import useServiceData from "pages/StandardTreatment/shared/useServiceData";
import useBootstrapModal from "pages/StandardTreatment/shared/useBootstrapModal";
import { ACTIVE_FILTERS, ErrorState, useDebounced } from "pages/StandardTreatment/shared/ui";
import DrugFormModal, { DRUG_MODAL_ID } from "./components/DrugFormModal";

const LIMIT = 10;

const MasterObat = () => {
  const [keyword, setKeyword] = useState("");
  const [isActive, setIsActive] = useState("");
  const [page, setPage] = useState(1);
  const search = useDebounced(keyword.trim());
  const { modal, open, setMode, close } = useBootstrapModal(DRUG_MODAL_ID);

  useEffect(() => {
    document.title = "SISMAL | Master Obat";
  }, []);

  // GET /drugs — searchBy/searchValue: "case-insensitive contains" per kolom.
  const { data, loading, error, reload } = useServiceData(
    () =>
      standardTreatmentService.listDrugs({
        page,
        limit: LIMIT,
        is_active: isActive || undefined,
        ...(search && {
          searchBy: "code,name,medication_key",
          searchValue: [search, search, search].join(","),
        }),
      }),
    [page, isActive, search]
  );

  const columns = useMemo(
    () => [
      { header: "Kode", accessorKey: "code", size: 90 },
      { header: "Nama Obat", accessorKey: "name", size: 180 },
      { header: "Bentuk Sediaan", accessorKey: "dosage_form", size: 140, cell: ({ getValue }) => getValue() || "—" },
      {
        header: "Medication Key",
        accessorKey: "medication_key",
        size: 170,
        cell: ({ getValue }) =>
          getValue() ? <code className="md-code">{getValue()}</code> : <span className="mps-pill is-warning">Belum dipetakan</span>,
      },
      {
        header: "Status",
        accessorKey: "is_active",
        size: 90,
        cell: ({ getValue }) => (
          <span className={`mps-pill ${getValue() ? "is-success" : "is-neutral"}`}>{getValue() ? "Aktif" : "Nonaktif"}</span>
        ),
      },
      {
        header: "Aksi",
        size: 150,
        cell: ({ row }) => (
          <div className="row-actions">
            <ActionButton btnType="detail" onClick={() => open("detail", row.original)} />
            <ActionButton btnType="edit" onClick={() => open("edit", row.original)} />
          </div>
        ),
      },
    ],
    []
  );

  const tableInstance = useReactTable({ data: data?.drugs || [], columns, getCoreRowModel: getCoreRowModel() });

  // POST /drug atau PATCH /drug/{id}; error ditampilkan di modal.
  const saveDrug = (body) =>
    (modal.mode === "edit"
      ? standardTreatmentService.updateDrug(modal.item.id, body)
      : standardTreatmentService.createDrug(body)
    ).then(() => {
      close();
      toast.success(`Obat ${body.name} berhasil disimpan`);
      reload();
    });

  return (
    <div className="master-data-page">
      <h1 className="page-title">Master Obat</h1>
      <p className="page-subtitle">
        Master data bersama untuk semua Versi. Dipakai oleh Terapi melalui Aturan Dosis. Obat tidak
        dihapus — nonaktifkan bila sudah tidak dipakai.
      </p>

      <div className="action-wrapper">
        <Button variant="primary" size="normal" onClick={() => open("add")}>
          <FiPlus size={16} className="mr-2" />
          Tambah Obat
        </Button>
        <div className="action-filters">
          <div className="action-search">
            <InputField
              placeholder="Cari kode, nama, atau medication key..."
              value={keyword}
              onChange={(e) => {
                setKeyword(e.target.value);
                setPage(1);
              }}
            />
          </div>
          <div className="action-status">
            <InputSelect
              isClearable={false}
              data={ACTIVE_FILTERS}
              value={ACTIVE_FILTERS.find((o) => o.value === isActive)}
              onChange={(sel) => {
                setIsActive(sel?.value ?? "");
                setPage(1);
              }}
            />
          </div>
        </div>
      </div>

      {error ? (
        <div className="mt-4">
          <ErrorState messages={error} onRetry={reload} />
        </div>
      ) : (
        <TableDataComponent
          columnLength={columns.length}
          tableInstance={tableInstance}
          onPageChange={(next) => next && setPage(next)}
          loading={loading}
          currentPage={data?.currentPage ?? page}
          limit={LIMIT}
          nextPage={data?.nextPage}
          previousPage={data?.previousPage}
          total={data?.total ?? 0}
        />
      )}

      <DrugFormModal
        key={modal.openCount}
        mode={modal.mode}
        drug={modal.item}
        onSubmit={saveDrug}
        onEdit={() => setMode("edit")}
      />
    </div>
  );
};

export default MasterObat;
